import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminUserForm from '../../admin/components/access/AdminUserForm';
import AdminUsersTable from '../../admin/components/access/AdminUsersTable';
import {
  listAdminUsers,
  listRoles,
  createAdminUser,
  updateAdminUser,
  updateAdminUserStatus,
  deleteAdminUser,
} from '../../admin/api/accessApi';

const emptyForm = {
  fullName: '',
  email: '',
  temporaryPassword: '',
  adminType: 'SUB_ADMIN',
  isActive: true,
  mustChangePassword: true,
  roleIds: [],
};

const AdminSubAdmins = () => {
  const [filters, setFilters] = useState({ type: '', status: '', role: '', search: '' });
  const [appliedFilters, setAppliedFilters] = useState({ type: '', status: '', role: '', search: '' });
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const roleOptions = useMemo(
    () => roles.filter((role) => role.isActive !== false),
    [roles]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes] = await Promise.all([listAdminUsers(appliedFilters), listRoles()]);
      setUsers(usersRes?.data || []);
      setRoles(rolesRes?.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load admin users.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = async () => {
    setAppliedFilters(filters);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setForm({
      fullName: user.fullName || '',
      email: user.email || '',
      temporaryPassword: '',
      adminType: user.adminType || 'SUB_ADMIN',
      isActive: Boolean(user.isActive),
      mustChangePassword: Boolean(user.mustChangePassword),
      roleIds: (user.roles || []).map((role) => role.id),
    });
    setFormOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.fullName || !form.email || (!editingId && !form.temporaryPassword)) {
      setError('Full name, email, and password are required.');
      return;
    }
    if (form.adminType === 'SUB_ADMIN' && !form.roleIds.length) {
      setError('At least one role is required for a Sub Admin.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        temporaryPassword: form.temporaryPassword,
        adminType: form.adminType,
        isActive: form.isActive,
        mustChangePassword: form.mustChangePassword,
        roleIds: form.adminType === 'MASTER_ADMIN' ? ['MASTER_ADMIN'] : form.roleIds,
      };
      if (editingId) {
        await updateAdminUser(editingId, payload);
      } else {
        await createAdminUser(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Unable to save admin user.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRole = (roleId) => {
    setForm((prev) => {
      const set = new Set(prev.roleIds);
      if (set.has(roleId)) set.delete(roleId);
      else set.add(roleId);
      return { ...prev, roleIds: Array.from(set) };
    });
  };

  const handleStatus = async (user) => {
    try {
      await updateAdminUserStatus(user.id, { isActive: !user.isActive });
      await load();
    } catch (err) {
      setError(err.message || 'Unable to update status.');
    }
  };

  const handleResetPassword = async (user) => {
    const temporaryPassword = window.prompt(`Set a temporary password for ${user.fullName}`, '');
    if (!temporaryPassword) return;
    try {
      await updateAdminUser(user.id, {
        fullName: user.fullName,
        email: user.email,
        adminType: user.adminType,
        isActive: user.isActive,
        mustChangePassword: true,
        roleIds: (user.roles || []).map((role) => role.id),
        temporaryPassword,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Unable to reset password.');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.fullName}?`)) return;
    try {
      await deleteAdminUser(user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Unable to delete admin user.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[color:var(--admin-icon)]">Access Control</p>
          <h1 className="mt-2 text-2xl font-semibold text-[color:var(--admin-heading)]">Master Admin / Sub Admins</h1>
          <p className="mt-2 text-sm text-[color:var(--admin-text)]">
            Manage admin users, multiple role assignments, active status, and password reset requirements.
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="rounded-full bg-[var(--admin-accent)] px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-white"
        >
          Add Admin
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {error}
        </div>
      )}

      <div className="grid gap-3 rounded-[28px] border border-[color:var(--admin-border)] bg-white p-5 shadow-sm xl:grid-cols-5">
        <select
          value={filters.type}
          onChange={(event) => handleFilterChange('type', event.target.value)}
          className="rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
        >
          <option value="">All types</option>
          <option value="MASTER_ADMIN">MASTER_ADMIN</option>
          <option value="SUB_ADMIN">SUB_ADMIN</option>
        </select>
        <select
          value={filters.status}
          onChange={(event) => handleFilterChange('status', event.target.value)}
          className="rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
        >
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={filters.role}
          onChange={(event) => handleFilterChange('role', event.target.value)}
          className="rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
        >
          <option value="">All roles</option>
          {roleOptions.map((role) => (
            <option key={role.id} value={role.roleCode}>
              {role.roleName}
            </option>
          ))}
        </select>
        <input
          value={filters.search}
          onChange={(event) => handleFilterChange('search', event.target.value)}
          placeholder="Search name or email"
          className="rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-full border border-[color:var(--admin-border)] px-4 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--admin-heading)]"
        >
          Apply filters
        </button>
      </div>

      {formOpen && (
        <AdminUserForm
          form={form}
          roles={roleOptions}
          editing={Boolean(editingId)}
          saving={saving}
          onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
          onToggleRole={handleToggleRole}
          onSubmit={handleSave}
          onCancel={resetForm}
        />
      )}

      <AdminUsersTable
        users={users}
        loading={loading}
        onEdit={startEdit}
        onToggleStatus={handleStatus}
        onResetPassword={handleResetPassword}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AdminSubAdmins;
